import React from 'react'

import { isAxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react'

import { CreateApiKeyResponse } from '@/api/types'
import { useCreateApiKey } from '@/react-query/api-settings'
import { CopyIconSVG } from '@/views/assets/CopyIconSVG'

interface CreateApiKeyForm {
  name: string
}

export const CreateApiKeyModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const toast = useToast()
  const createApiKey = useCreateApiKey()
  const [createdKey, setCreatedKey] = React.useState<
    CreateApiKeyResponse['apiKey'] | null
  >(null)
  const [copied, setCopied] = React.useState(false)

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setError,
    reset,
  } = useForm<CreateApiKeyForm>({
    defaultValues: { name: '' },
  })

  const handleClose = () => {
    setCreatedKey(null)
    setCopied(false)
    reset()
    onClose()
  }

  const onSubmit = async ({ name }: CreateApiKeyForm) => {
    try {
      const response = await createApiKey.mutateAsync({ name })
      setCreatedKey(response.apiKey)
    } catch (error) {
      if (isAxiosError(error) && error.response?.data?.error?.message) {
        setError('name', {
          type: 'manual',
          message: error.response.data.error.message,
        })
      } else {
        setError('name', {
          type: 'manual',
          message: 'Something went wrong',
        })
      }
    }
  }

  const handleCopy = async () => {
    if (!createdKey) return
    try {
      await navigator.clipboard.writeText(createdKey.key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the key manually',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay bg="whiteAlpha.600" backdropFilter={'auto'} />
      <ModalContent
        bgColor={'#f5f5f5'}
        boxShadow={'0px 4px 30px 0px rgba(0, 0, 0, 0.25)'}
        rounded={'2.5rem'}
        w={'480px'}
        p={3}
      >
        {createdKey ? (
          <>
            <ModalHeader>API Key Created</ModalHeader>
            <ModalCloseButton rounded={'full'} mt={5} mr={5} />
            <ModalBody display={'flex'} flexDirection={'column'}>
              <Box
                bg="orange.50"
                border="1px"
                borderColor="orange.200"
                rounded="md"
                p={3}
                mb={4}
              >
                <Text fontWeight="500" color="orange.800">
                  Copy your API key now. You won&apos;t be able to see it again.
                </Text>
              </Box>
              <FormLabel fontWeight={'400'} color={'black'}>
                API Key
              </FormLabel>
              <Flex gap={2} align="center">
                <Input
                  value={createdKey.key}
                  readOnly
                  variant={'brand-solid-white'}
                  fontFamily="mono"
                  fontSize="sm"
                  cursor="pointer"
                  onClick={handleCopy}
                  _hover={{ borderColor: 'gray.400' }}
                  flex={1}
                />
                <IconButton
                  aria-label="Copy API key"
                  icon={
                    copied ? (
                      <CheckIcon color="green.500" />
                    ) : (
                      <CopyIconSVG color="gray" width="1rem" />
                    )
                  }
                  onClick={handleCopy}
                  variant="ghost"
                  size="md"
                  rounded="full"
                  flexShrink={0}
                />
              </Flex>
              <Box h={5} mt={1}>
                <Text
                  fontSize="sm"
                  color="green.500"
                  fontWeight="500"
                  opacity={copied ? 1 : 0}
                  transform={copied ? 'translateY(0)' : 'translateY(-4px)'}
                  transition="all 0.2s ease"
                >
                  Copied to clipboard!
                </Text>
              </Box>
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'}>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid"
                onClick={handleClose}
              >
                Done
              </Button>
            </ModalFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader>Create API Key</ModalHeader>
            <ModalCloseButton rounded={'full'} mt={5} mr={5} />
            <ModalBody display={'flex'} flexDirection={'column'}>
              <FormControl isInvalid={Boolean(errors.name)}>
                <FormLabel fontWeight={'400'} htmlFor="name" color={'black'}>
                  Name
                </FormLabel>
                <Input
                  id="name"
                  placeholder="e.g. n8n integration"
                  {...register('name', {
                    required: 'Name is required',
                    maxLength: {
                      value: 255,
                      message: 'Name must be 255 characters or less',
                    },
                  })}
                  variant={'brand-solid-white'}
                />
                <FormErrorMessage>
                  {errors.name && errors.name.message}
                </FormErrorMessage>
              </FormControl>
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'}>
              <Button
                type="submit"
                variant={'brand-solid'}
                isLoading={isSubmitting}
                rounded={'1.25rem'}
              >
                Create
              </Button>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid-white"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  )
}
